import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { signUp, signIn, refreshVerification } from '../AuthContext';
import { API_BASE } from '../data/env';
import { getAccessInvite, acceptAccessInvite } from '../data/firestore';
import AuthShell from '../components/AuthShell';
import Clickwrap from '../components/Clickwrap';
import { acceptLegal, fetchAgreements } from '../data/legal';

// Invite landing (/welcome?invite=TOKEN): the invitee's whitelisted email is
// pre-filled from the invite doc (the token is the secret); they only choose
// a password. The accessGrants doc already exists, so account creation is all
// it takes — the rules admit them immediately.
export default function Welcome() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(search).get('invite');
  const [invite, setInvite] = useState(undefined); // undefined=loading, null=invalid
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    if (!token) {
      setInvite(null);
      return;
    }
    getAccessInvite(token).then(setInvite).catch(() => setInvite(null));
  }, [token]);

  const review = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const list = await fetchAgreements();
      if (!list.length) throw new Error('Agreements are not available right now.');
      setDocs(list);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const finish = async (seen) => {
    setError(null);
    setBusy(true);
    try {
      try {
        await signUp(invite.email, password);
      } catch (err) {
        // Re-used link after the account exists — sign in with the same
        // credentials instead of failing.
        if (err.code === 'auth/email-already-in-use') {
          await signIn(invite.email, password);
        } else {
          throw err;
        }
      }
      // The invite token is the proof this address was asked in. The
      // backend verifies the email, seats the organization, and grants
      // instance sign-in before we leave this page — so the landing
      // (often "sign in to the app with nventr") is not a second wait.
      if (!auth.currentUser) throw new Error('Account created, but the session did not start. Open the invite link and sign in with the password you just chose.');
      const idToken = await auth.currentUser.getIdToken();
      const act = await fetch(`${API_BASE}/auth/invites/${token}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const actBody = await act.json().catch(() => ({}));
      if (!act.ok) throw new Error(actBody.error || 'Could not finish the invite');
      if (!(await refreshVerification())) throw new Error('Your account was created, but email verification did not stick. Open the invite link again.');
      await acceptAccessInvite(token);
      await acceptLegal({ scope: 'user', docIds: seen.map((d) => d.id) });
      // /console drops them into their organization when they already have
      // a seat (org invite accepted alongside the access invite) and shows
      // the org list only when they have none.
      navigate(invite.landing || '/console', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (invite === undefined) {
    return (
      <div className="cs-auth-wrap">
        <div className="cs-empty">Checking your invite…</div>
      </div>
    );
  }
  if (invite === null) {
    return (
      <div className="cs-auth-wrap">
        <div className="cs-empty" style={{ maxWidth: 480 }}>
          This invite link isn't valid — it may have been revoked. Contact
          Nventr for a fresh one.
        </div>
      </div>
    );
  }

  return (
    <AuthShell>
      <div className="cs-modal" style={{ maxWidth: docs ? 960 : 420 }}>
        <div className="cs-brand" style={{ marginBottom: 6 }}>
          <img
            src="/logo.png"
            alt={process.env.REACT_APP_APP_NAME || 'nventr.io'}
            className="cs-brand-logo"
            style={{ height: 32 }}
          />
        </div>
        <h2 style={{ fontSize: 22, marginTop: 0 }}>Welcome — you're invited</h2>
        <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: 14 }}>
          Your email is already approved. Choose a password to activate your
          account.{invite.note ? ` ${invite.note}` : ''}
        </p>

        {error && (
          <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>
            {error}
          </div>
        )}

        {docs ? (
          <Clickwrap
            docs={docs}
            busy={busy}
            intro="Read to the end, then agree: the terms, privacy policy, and end user agreement."
            onAgree={finish}
          />
        ) : (
        <form onSubmit={review}>
          <div className="cs-field">
            <label>Email</label>
            <input type="email" className="cs-input" value={invite.email} readOnly disabled />
          </div>
          <div className="cs-field">
            <label>Choose a password</label>
            <input
              type="password"
              className="cs-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              autoFocus
            />
          </div>
          <button type="submit" className="cs-btn cs-btn-primary" style={{ width: '100%' }} disabled={busy || password.length < 6}>
            {busy ? 'Loading agreements…' : 'Review agreements'}
          </button>
        </form>
        )}

        {invite.status === 'accepted' && (
          <p style={{ textAlign: 'center', marginTop: 14, fontSize: 12.5, color: 'var(--muted)' }}>
            This invite was already used — entering the same password signs
            you back in.
          </p>
        )}
      </div>
    </AuthShell>
  );
}
